/**
 * @file bit to work with cookies
 */

const getCookie = (name: string): string => {
  const v = document.cookie.match('(^|;) ?' + name + '=([^;]*)(;|$)')
  return v != null ? v[2] : ''
}

/**
 * Every cookie whose name starts with `prefix`, keyed by the rest of the name. For a family of
 * cookies whose members are not known up front — one per running experiment, say — where
 * getCookie cannot be called because the caller does not know the names to ask for.
 *
 * Values are returned raw, so a caller that writes encoded values decodes them itself. Two
 * cookies sharing a name (same name, different paths) collapse to the first, which is the
 * more specific path.
 */
function getCookiesWithPrefix(prefix: string): Record<string, string> {
  const found: Record<string, string> = {}
  for (const pair of document.cookie.split(';')) {
    const separator = pair.indexOf('=')
    if (separator <= 0) continue
    const name = pair.slice(0, separator).trim()
    if (!name.startsWith(prefix)) continue
    const suffix = name.slice(prefix.length)
    // `Object.hasOwn`, not `in`: `in` walks the prototype chain, so a cookie whose suffix is
    // `constructor` or `toString` would read as already seen and be dropped on first sight.
    if (suffix === '' || Object.hasOwn(found, suffix)) continue
    found[suffix] = pair.slice(separator + 1).trim()
  }
  return found
}

interface SetCookieOptions {
  maxAgeInSeconds?: number
  expires?: string
  path?: string
  domain?: string
}

function buildCookieString(name: string, value: string, options: SetCookieOptions = {}): string {
  const maxAgeString = options.maxAgeInSeconds != null ? `;max-age=${options.maxAgeInSeconds}` : ''
  const expiresString = options.expires ? `;expires=${options.expires}` : ''
  const pathString = options.path ? `;path=${options.path}` : ';path=/'
  const domainString = options.domain ? `;domain=${options.domain}` : ''
  return `${name}=${value}${maxAgeString}${expiresString}${pathString}${domainString}`
}

function setCookie(name: string, value: string, options: SetCookieOptions = {}): void {
  document.cookie = buildCookieString(name, value, options)
}

function deleteCookie(name: string): void {
  setCookie(name, '', {
    maxAgeInSeconds: 0,
    expires: 'Thu, 01 Jan 1970 00:00:00 GMT',
    path: '/',
  })
}

function getExperimentVariantCookie(experimentName: string): string {
  try {
    const parsed: unknown = JSON.parse(decodeURIComponent(getCookie('ww_x')))
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return ''
    const variant: unknown = Reflect.get(parsed, experimentName)
    return typeof variant === 'string' ? variant : ''
  } catch {
    return ''
  }
}

export { buildCookieString, deleteCookie, getCookie, getCookiesWithPrefix, getExperimentVariantCookie, setCookie }
